// src/components/dashboard/DashboardApprovals.tsx
// NEW — replaces the inline placeholder stub in App.tsx. Real home for
// chat-based pending approvals, closing the gap surfaced by a stress-
// tested user story: a stuck approval (e.g. "install this kit" left
// unresolved mid-conversation) was previously only discoverable by
// reopening the exact chat it happened in — nothing on Home, nowhere
// in the sidebar, no badge, surfaced it. This page queries
// GET /dashboard/chat/chats/pending, which scans the entity's 20 most
// recent chats for an unresolved trailing tool_use — same detection
// logic already proven correct in ChatScreen.tsx's live approval flow.
//
// Deliberately NOT unified with Timeline's thread-status filter. Per
// explicit design decision: MessageThreadSummary (counterparty
// negotiation state) and chat pendingApproval (a paused tool call in
// the owner's own conversation with FDW) are structurally different —
// no natural join between them. This page is scoped to chat approvals
// only. Timeline's "Needs Approval" sub-filter remains a separate,
// thread-status-based concept, not merged here.
//
// Clicking an item navigates into the actual chat (via
// /dashboard/chat-history/:chatId) rather than trying to resolve the
// approval inline on this page — ChatScreen.tsx already has the real
// Approve/Decline UI wired to the full conversation context; duplicating
// that here would mean re-deriving conversation state this page doesn't
// have and shouldn't need.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckSquare, ArrowRight } from 'lucide-react';
import { auth } from '../../services/firebase';

const FDW_BASE = 'https://fivedollarworkday.com';

let dashboardToken: string | null = null;

async function getDashboardToken(): Promise<string> {
  if (dashboardToken) return dashboardToken;
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not signed in.');
  const res = await fetch(`${FDW_BASE}/dashboard/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });
  if (!res.ok) throw new Error('Failed to establish dashboard session.');
  const data = await res.json();
  dashboardToken = data.access_token;
  return dashboardToken;
}

async function authedFetch(path: string, options: RequestInit = {}) {
  const token = await getDashboardToken();
  return fetch(`${FDW_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
}

interface PendingApprovalEntry {
  chatId: string;
  title: string;
  updatedAt: string | null;
  pendingApproval: {
    tool_use_id: string;
    name: string;
    input: any;
  };
}

// Human-readable labels for the tool names most likely to show up here —
// falls back to the raw tool name for anything not listed, so a new
// gated tool never silently renders as blank.
const TOOL_LABELS: Record<string, string> = {
  install_starter_kit: 'Install starter kit',
  publish_presence: 'Publish your presence',
  claim_domain: 'Claim custom domain',
  verify_domain_claim: 'Verify domain claim',
  add_team_member: 'Add team member',
  revoke_team_member: 'Revoke team member',
  commit_engagement: 'Commit to an engagement',
  send_message: 'Send a message',
};

function toolLabel(name: string): string {
  return TOOL_LABELS[name] ?? name;
}

export const DashboardApprovals: React.FC = () => {
  const [pending, setPending] = useState<PendingApprovalEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await authedFetch('/dashboard/chat/chats/pending');
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to load pending approvals.');
        }
        const data = await res.json();
        if (!cancelled) setPending(data.pending ?? []);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not load your approvals.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (isLoading) return <p className="text-sm text-brand-gray font-mono">Loading approvals…</p>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-brand-black">Approvals</h1>
        <p className="text-sm text-brand-charcoal font-light mt-1">
          Actions waiting on your decision, from conversations with FDW.
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {pending.length === 0 && !error && (
        <div className="rounded-xl border border-dashed border-brand-border bg-brand-surface p-10 text-center">
          <p className="text-sm text-brand-charcoal font-light">
            Nothing waiting on you right now. Actions like installing a kit or publishing a package will show up here until you approve or decline them.
          </p>
        </div>
      )}

      {pending.length > 0 && (
        <ul className="divide-y divide-brand-border rounded-xl border border-brand-border bg-brand-white">
          {pending.map((entry) => (
            <li key={entry.chatId}>
              <Link
                to={`/dashboard/chat-history/${encodeURIComponent(entry.chatId)}`}
                className="flex items-center justify-between p-4 hover:bg-brand-surface transition-colors"
              >
                <div className="flex items-start gap-3">
                  <CheckSquare className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-brand-black">
                      {toolLabel(entry.pendingApproval.name)}
                    </p>
                    <p className="text-xs text-brand-gray mt-0.5">
                      From "{entry.title}"
                      {entry.updatedAt && ` · ${new Date(entry.updatedAt).toLocaleString()}`}
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-brand-gray shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};